import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ErrorBanner, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import {
  ApiError,
  api,
  assistant,
  catalogs,
  policies,
  trust,
  type DeviceGroupSummary,
  type PublicationStatus,
  type ValidationMessage
} from '../../lib/api'
import { paramFrom, withParams } from '../../lib/tabs'
import { PublicationStatusCard } from './PublicationStatusCard'
import { GuidedPolicyEditor } from './GuidedPolicyEditor'
import { TemplatePicker } from './TemplatePicker'
import {
  PolicyParseError,
  type PolicyDraft,
  describeDraft,
  emptyDraft,
  parseDraft,
  serializeDraft,
  watcherAdvisories
} from './policyDocument'
import { type PublishTargets, describeTargets, targetsFrom, toAssignment } from './publishTargets'
import { publishedRevision } from './publishOutcome'

/**
 * The policy editor.
 *
 * The document is edited as a form, with the JSON kept as an advanced view rather than as the only
 * view. The reason for the form is not that JSON is hard to read: it is that a policy has perhaps
 * forty meaningful fields spread over a capability parameter block, a trigger, a condition expression,
 * a retry envelope and a detection dictionary, and every one of them has a value the agent accepts
 * and a value it silently ignores. A form can offer the twelve assistants the agent knows how to
 * find, the four trigger shapes, and the fourteen catalog rules; a text box can only offer a
 * validation error after the fact.
 *
 * The JSON view stays because two things need it. A policy from a newer agent may carry fields this
 * console has never heard of — the form preserves them but cannot show them — and an operator
 * diffing what was published against what is in front of them wants the bytes, not a rendering of
 * them. Switching to the form from unparseable JSON is refused rather than attempted, because the
 * alternative is a form quietly showing a document that is not the one in the box.
 */
export function PolicyEditorPage() {
  const { policyId } = useParams<{ policyId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // A draft handed over by the assistant arrives in the navigation state rather than in a query
  // string: it is a whole JSON document, and a URL is the wrong place for one.
  const handedOver = (location.state as { draft?: string } | null)?.draft

  const [draft, setDraft] = useState<PolicyDraft>(() => {
    if (!handedOver) return emptyDraft()
    try {
      return parseDraft(handedOver)
    } catch {
      return emptyDraft()
    }
  })
  const [json, setJson] = useState(() => (handedOver ? handedOver : serializeDraft(emptyDraft())))
  // A draft from the assistant opens as the document it is, not as a rendering of it. The promise on
  // that hand-over is that what is saved is exactly what was validated, and the form would re-emit it
  // in its own property order and fill in its own defaults for anything the assistant left out.
  const [advanced, setAdvanced] = useState(Boolean(handedOver))

  const [notes, setNotes] = useState('')
  // Whether the publish confirmation is open, and what the fleet is running while it is. Both are
  // cleared by publishing, so the next open asks the server again rather than showing the old answer.
  const [confirmingPublish, setConfirmingPublish] = useState(false)
  const [replacing, setReplacing] = useState<PublicationStatus | null>(null)
  const [findings, setFindings] = useState<ValidationMessage[]>([])
  const [status, setStatus] = useState<string>('draft')
  const [revision, setRevision] = useState<number | null>(null)
  // Bumped when a publish went out as a copy, so the page shows the revision that is now current.
  const [reloads, setReloads] = useState(0)
  const [savedPolicyId, setSavedPolicyId] = useState<string | null>(policyId ?? null)
  // Bumped after a publish so the reach card describes the revision that was just sent.
  const [publishedNonce, setPublishedNonce] = useState(0)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [explanation, setExplanation] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The builder group this policy belongs to, if the builder made it (ADR-S-052).
  const [group, setGroup] = useState<{ groupId: string; name: string } | null>(null)
  // Dictionaries chosen before the first save, bound in the same act as it.
  const [pendingBindings, setPendingBindings] = useState<string[]>([])
  const [boundCount, setBoundCount] = useState(0)
  // The violation message chosen before the first save, recorded in the same act as it (ADR-S-053).
  const [pendingViolationMessage, setPendingViolationMessage] = useState<string | null>(null)
  // Who a publish assigns the policy to. Read from what it is assigned to now when the dialog opens,
  // so republishing a policy aimed at two groups does not quietly widen it to every device.
  const [targets, setTargets] = useState<PublishTargets>({ kind: 'all' })
  const [keptTargets, setKeptTargets] = useState<PublishTargets | null>(null)
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroupSummary[] | null>(null)

  /**
   * Whether the full editor is showing. Open by default for a policy somebody is writing by hand,
   * which is what this page was before the builder, and folded for one the builder made, whose
   * every setting the builder already chose. In the URL, so the group page's "Advanced settings"
   * link lands with it open and a reload keeps it that way.
   */
  const advancedParam = paramFrom(location.search, 'advanced')
  const settingsOpen = advancedParam === '1' || (advancedParam === null && group === null)
  const setSettingsOpen = (open: boolean) =>
    navigate({ search: withParams(location.search, { advanced: open ? '1' : '0' }) }, { replace: true })

  const catalog = useAsync(() => catalogs.authoring(), [])
  const agents = useAsync(() => catalogs.agents(), [])

  useEffect(() => {
    if (!policyId) return
    api
      .getPolicy(policyId)
      .then((detail) => {
        setJson(detail.documentJson)
        setFindings(detail.validation)
        setStatus(detail.status)
        setRevision(detail.revision)
        setSavedPolicyId(detail.policyId)
        setGroup(detail.groupId ? { groupId: detail.groupId, name: detail.groupName ?? 'its group' } : null)
        try {
          setDraft(parseDraft(detail.documentJson))
        } catch {
          // A stored document the form cannot represent opens in the JSON view rather than being
          // shown as something it is not. This is what makes the editor safe against a policy written
          // by a newer agent than this console.
          setAdvanced(true)
        }
      })
      .catch((error: unknown) => setFailure(error instanceof ApiError ? error.message : 'The policy could not be loaded.'))
  }, [policyId, reloads])

  // The form owns the document while it is open, so the JSON follows it. In the JSON view the box
  // owns the document and the form follows on the way back, which is the only direction that can fail.
  useEffect(() => {
    if (!advanced) setJson(serializeDraft(draft))
  }, [draft, advanced])

  // Validation is debounced rather than run on every keystroke: it is a round trip, and an editor
  // that fires one per character is both slow and impossible to read while typing.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      api
        .validatePolicy(json)
        .then((result) => setFindings(result.messages))
        .catch(() => {
          /* A failed validation call must not replace the editor's contents with an error. */
        })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [json])

  const run = useCallback(async (work: () => Promise<string>) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setNotice(await work())
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      if (error instanceof ApiError && error.validation.length > 0) setFindings(error.validation)
    } finally {
      setBusy(false)
    }
  }, [])

  const showForm = () => {
    try {
      setDraft(parseDraft(json))
      setAdvanced(false)
      setFailure(null)
    } catch (error) {
      setFailure(
        error instanceof PolicyParseError
          ? `This document cannot be shown as a form until it parses: ${error.message}`
          : 'This document cannot be shown as a form.'
      )
    }
  }

  const save = () =>
    run(async () => {
      const result = await api.savePolicy(json, savedPolicyId ?? undefined, notes || undefined)
      setSavedPolicyId(result.policyId)
      setRevision(result.revision)
      setStatus('draft')
      setFindings(result.validation)
      // Bound before navigating, because the page for the saved policy reads its bindings from the
      // server and would otherwise open showing none of the ones just chosen.
      let bound = ''
      if (!savedPolicyId && pendingBindings.length > 0) {
        try {
          await policies.bind(result.policyId, pendingBindings)
          bound = ` ${pendingBindings.length} ${pendingBindings.length === 1 ? 'dictionary is' : 'dictionaries are'} bound.`
          setPendingBindings([])
        } catch (error) {
          bound = ` The dictionaries could not be bound (${error instanceof ApiError ? error.message : 'the request failed'}); bind them again below.`
        }
      }
      if (!savedPolicyId && pendingViolationMessage !== null) {
        try {
          await policies.useViolationMessage(result.policyId, pendingViolationMessage)
          bound += ' The chosen violation message is recorded.'
          setPendingViolationMessage(null)
        } catch (error) {
          bound += ` The violation message could not be recorded (${error instanceof ApiError ? error.message : 'the request failed'}); choose it again below.`
        }
      }
      if (!policyId) navigate(`/policies/${result.policyId}`, { replace: true })
      return `Saved revision ${result.revision}.${bound}`
    })

  const approve = () =>
    run(async () => {
      if (!savedPolicyId || revision === null) throw new ApiError(400, 'Save the policy before approving it.')
      await api.approvePolicy(savedPolicyId, revision)
      setStatus('approved')
      return `Revision ${revision} is approved and ready to publish.`
    })

  /**
   * Opens the confirmation, and asks what is running now so the question can be answered properly.
   *
   * "Publish" is the one button on this screen that reaches a machine, and it assigns to every
   * enrolled device. An operator who has just edited a policy that four hundred devices are running
   * is entitled to be told that before it happens rather than after, and the number is the whole
   * difference between a rollout and an experiment. Never published is not an error here - it is the
   * ordinary case for a first publish - so a 404 leaves the count absent and the dialog says so.
   */
  const askToPublish = async () => {
    setConfirmingPublish(true)
    setReplacing(null)
    setTargets({ kind: 'all' })
    setKeptTargets(null)
    catalogs
      .deviceGroups()
      .then(setDeviceGroups)
      .catch(() => setDeviceGroups([]))
    if (!savedPolicyId) return
    try {
      const current = await trust.publication(savedPolicyId)
      setReplacing(current)
      const now = targetsFrom(current.targets)
      setTargets(now)
      setKeptTargets(now.kind === 'keep' ? now : null)
    } catch {
      setReplacing(null)
    }
  }

  const publish = () =>
    run(async () => {
      if (!savedPolicyId || revision === null) throw new ApiError(400, 'Save and approve the policy before publishing it.')
      // The assignment replaces the last one, so it always states the whole intended set.
      await api.assignPolicy(savedPolicyId, toAssignment(targets))
      const published = await api.publishPolicy(savedPolicyId, revision)
      setPublishedNonce((current) => current + 1)
      if (published.revisedFrom != null) setReloads((current) => current + 1)
      const warnings = published.warnings.length > 0 ? ` ${published.warnings.join(' ')}` : ''
      const sentence = publishedRevision(published.revision, published.revisedFrom)
      return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)} (${published.compiledBytes} bytes).${warnings}`
    })

  /**
   * "What does this one actually do?"
   *
   * On the editor rather than only on the assistant page, because that is where the question is
   * asked — by the administrator who inherited forty policies somebody else wrote. It explains the
   * saved revision, so it says what the policy *is* rather than what the unsaved text in the box
   * might become.
   */
  const explain = () =>
    run(async () => {
      if (!savedPolicyId) throw new ApiError(400, 'Save the policy before asking about it.')
      const result = await assistant.explainPolicy(savedPolicyId)
      setExplanation(result.succeeded ? result.explanation : result.problem)
      // The explanation is the answer; the notice line stays for the actions that changed something.
      return result.succeeded ? 'Explained.' : 'The assistant could not explain this one.'
    })

  const fromTemplate = (templateKey: string, agentKey?: string) =>
    run(async () => {
      const created = await policies.fromTemplate(templateKey, agentKey)
      navigate(`/policies/${created.policyId}`, { replace: true })
      return `Started from the '${templateKey}' template. It is saved as a draft and has not been published.`
    })

  const errors = findings.filter((finding) => finding.severity === 'error')
  const warnings = findings.filter((finding) => finding.severity === 'warning')
  // Read from the form's draft, so in the JSON view they wait until the document is a form again. A
  // builder group's members are left alone: the builder chose their settings.
  const advisories = advanced || group ? [] : watcherAdvisories(draft, boundCount)

  const sentence = useMemo(
    () =>
      catalog.value
        ? describeDraft(
            draft,
            (capability) =>
              catalog.value!.capabilities.find((entry) => entry.name === capability)?.displayName ?? capability
          )
        : null,
    [draft, catalog.value]
  )

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{draft.name || (policyId ? 'Edit policy' : 'New policy')}</h1>
          <p>
            {revision === null ? 'Unsaved draft' : `Revision ${revision}`}
            {' · '}
            <span className={status === 'approved' ? 'pill sev-2' : 'pill'}>{status}</span>
          </p>
        </div>
        <div className="toolbar">
          {settingsOpen && (
            <div className="view-switch" role="group" aria-label="How to edit this policy">
              <button
                type="button"
                className={advanced ? '' : 'primary'}
                onClick={showForm}
                aria-pressed={!advanced}
                data-testid="show-form"
              >
                Form
              </button>
              <button
                type="button"
                className={advanced ? 'primary' : ''}
                onClick={() => setAdvanced(true)}
                aria-pressed={advanced}
                data-testid="show-json"
              >
                JSON
              </button>
            </div>
          )}
          <button type="button" onClick={save} disabled={busy || errors.length > 0} data-testid="save-policy">
            Save draft
          </button>
          <button type="button" onClick={approve} disabled={busy || revision === null || status === 'approved'} data-testid="approve-policy">
            Approve
          </button>
          {group ? (
            // A member is published with its siblings, so Claude in a browser cannot go out while
            // the desktop app it was chosen alongside stays a draft.
            <Link className="button primary" to={`/policies/groups/${group.groupId}`} data-testid="publish-with-group">
              Publish with its group
            </Link>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={askToPublish}
              disabled={busy || status !== 'approved'}
              data-testid="publish-policy"
            >
              Publish
            </button>
          )}
          <button type="button" onClick={explain} disabled={busy || !savedPolicyId} data-testid="explain-this-policy">
            Explain
          </button>
        </div>
      </div>

      {sentence && (
        <p className="lede" data-testid="policy-sentence">
          {sentence}
        </p>
      )}

      {group && (
        <div className="banner" role="note" data-testid="policy-group-link">
          Part of <Link to={`/policies/groups/${group.groupId}`}>{group.name}</Link>, made with the policy builder.
          It is approved and published together with the other policies in that group.
        </div>
      )}

      <ErrorBanner message={failure} />
      <ErrorBanner message={catalog.error ?? agents.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="policy-notice">
          {notice}
        </div>
      )}
      {explanation && (
        <div className="banner" data-testid="policy-explanation">
          {explanation}
        </div>
      )}

      {!policyId && !savedPolicyId && (
        <p className="hint" data-testid="builder-suggestion">
          Most policies are quicker to make with the <Link to="/policies/build">policy builder</Link>, which asks
          what to watch, where it runs, and what to do.
        </p>
      )}

      <div className="advanced-toggle">
        <button
          type="button"
          aria-expanded={settingsOpen}
          onClick={() => setSettingsOpen(!settingsOpen)}
          data-testid="advanced-settings-toggle"
        >
          {settingsOpen ? 'Hide advanced settings' : 'Advanced settings'}
        </button>
        {!settingsOpen && (
          <span className="hint">
            Selectors, timing, conditions, retry and the document itself. The builder already chose all of these.
          </span>
        )}
      </div>

      {settingsOpen && !policyId && !savedPolicyId && (
        <TemplatePicker disabled={busy} agents={agents.value ?? []} onChoose={fromTemplate} />
      )}

      <div className="editor-grid">
        <div>
          <div data-testid="advanced-settings" hidden={!settingsOpen}>
            {!advanced && (!catalog.value || !agents.value) ? (
              // The form is what this page is; showing the JSON box for the half-second the catalog
              // takes to arrive would put a text area under the cursor and then take it away again.
              <div className="card">
                <div className="card-body">
                  <div className="empty">Loading the editor…</div>
                </div>
              </div>
            ) : advanced ? (
              <div className="card">
                <div className="card-body">
                  <label htmlFor="document">Policy document</label>
                  <textarea
                    id="document"
                    name="document"
                    spellCheck={false}
                    value={json}
                    onChange={(event) => setJson(event.target.value)}
                    data-testid="policy-document"
                  />
                  <div className="hint">
                    Every key is preserved on save, including any this console does not manage, so a
                    policy written for a newer agent survives being edited here.
                  </div>
                </div>
              </div>
            ) : (
              <GuidedPolicyEditor
                draft={draft}
                catalog={catalog.value!}
                agents={agents.value!}
                policyId={savedPolicyId}
                pendingBindings={pendingBindings}
                onPendingBindingsChange={setPendingBindings}
                onBoundCountChange={setBoundCount}
                pendingViolationMessage={pendingViolationMessage}
                onPendingViolationMessageChange={setPendingViolationMessage}
                onChange={setDraft}
              />
            )}
          </div>

          <div className="card" style={{ marginTop: settingsOpen ? '1rem' : 0 }}>
            <div className="card-body">
              <div className="field">
                <label htmlFor="notes">Revision notes</label>
                <input
                  id="notes"
                  name="notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="What changed and why"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card sticky">
          <div className="card-body">
            <h3 style={{ marginTop: 0 }}>Validation</h3>
            {errors.length === 0 && warnings.length === 0 && (
              <div className={advisories.length === 0 ? 'banner success' : 'banner'} data-testid="validation-clean">
                {advisories.length === 0
                  ? 'This document is valid and the agent will accept it.'
                  : 'The agent will accept this document, but as written it does little.'}
              </div>
            )}

            {advisories.length > 0 && (
              <div data-testid="validation-advisories" style={{ marginTop: '0.85rem' }}>
                <div className="banner warning">
                  {advisories.length === 1 ? 'One thing' : `${advisories.length} things`} to check before publishing.
                  These do not block saving.
                </div>
                {advisories.map((advisory) => (
                  <div className="finding" key={advisory}>
                    <div>{advisory}</div>
                  </div>
                ))}
              </div>
            )}

            {errors.length > 0 && (
              <div data-testid="validation-errors">
                <div className="banner error">
                  {errors.length} error{errors.length === 1 ? '' : 's'} must be fixed before this can be saved.
                </div>
                {errors.map((finding, index) => (
                  <Finding key={`error-${index}`} finding={finding} />
                ))}
              </div>
            )}

            {warnings.length > 0 && (
              <div data-testid="validation-warnings" style={{ marginTop: '0.85rem' }}>
                <div className="banner warning">
                  {warnings.length} warning{warnings.length === 1 ? '' : 's'}. These do not block publication.
                </div>
                {warnings.map((finding, index) => (
                  <Finding key={`warning-${index}`} finding={finding} />
                ))}
              </div>
            )}
          </div>

          {savedPolicyId ? (
            <PublicationStatusCard policyId={savedPolicyId} reloadKey={publishedNonce} />
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmingPublish}
        title={`Publish revision ${revision ?? ''} to the fleet`}
        confirmLabel={
          targets.kind === 'all' ? 'Publish to every device' : `Publish to ${describeTargets(targets, deviceGroups ?? [])}`
        }
        busy={busy}
        confirmDisabled={targets.kind === 'groups' && targets.groupIds.length === 0}
        // Closed before publishing rather than after. A refused publish reports on the page, and a
        // modal still covering it would be an operator told nothing at all.
        onConfirm={() => {
          setConfirmingPublish(false)
          void publish()
        }}
        onClose={() => setConfirmingPublish(false)}
        data-testid="publish-confirm"
      >
        <PublishTargetPicker targets={targets} kept={keptTargets} groups={deviceGroups} onChange={setTargets} />
        <p>
          Each device it is assigned to is handed the new envelope at its next check-in and starts
          running it then.
        </p>

        {replacing && !replacing.revokedUtc ? (
          <div className="banner warning" role="note" data-testid="publish-replacing">
            Revision {replacing.revision} is published now, and {replacing.devicesReportingActive}{' '}
            {replacing.devicesReportingActive === 1 ? 'device is' : 'devices are'} reporting it
            running. They stop running it and start running this one. A policy that turns out to be
            wrong is corrected by publishing again, which is another rollout rather than an undo.
          </div>
        ) : (
          <p className="hint" data-testid="publish-first-time">
            Nothing is published for this policy today, so nothing stops running.
          </p>
        )}
      </ConfirmDialog>
    </>
  )
}

function Finding({ finding }: { finding: ValidationMessage }) {
  return (
    <div className="finding">
      <code>{finding.fieldPath || '(document)'}</code>
      <div>
        <div>{finding.message}</div>
        {finding.suggestion && <div className="muted">{finding.suggestion}</div>}
      </div>
    </div>
  )
}

/**
 * Who the publish reaches: every device, or the device groups ticked here.
 *
 * Assignments to single devices can be made through the API and are not offered here, but a policy
 * that already has them keeps them: "keep" re-sends exactly what it is assigned to now rather than
 * dropping the devices because this dialog cannot draw them.
 */
function PublishTargetPicker({
  targets,
  kept,
  groups,
  onChange
}: {
  targets: PublishTargets
  /** What it is assigned to now, when that includes something this dialog cannot draw. */
  kept: PublishTargets | null
  groups: DeviceGroupSummary[] | null
  onChange: (targets: PublishTargets) => void
}) {
  const chosen = targets.kind === 'groups' ? targets.groupIds : []
  return (
    <fieldset className="field" data-testid="publish-targets">
      <legend>Who gets it</legend>
      {kept && (
        <label className="inline-check">
          <input
            type="radio"
            name="publish-target"
            checked={targets.kind === 'keep'}
            onChange={() => onChange(kept)}
            data-testid="publish-target-keep"
          />
          <span>What it is assigned to now, including single devices</span>
        </label>
      )}
      <label className="inline-check">
        <input
          type="radio"
          name="publish-target"
          checked={targets.kind === 'all'}
          onChange={() => onChange({ kind: 'all' })}
          data-testid="publish-target-all"
        />
        <span>Every enrolled device in this organization</span>
      </label>
      <label className="inline-check">
        <input
          type="radio"
          name="publish-target"
          checked={targets.kind === 'groups'}
          disabled={groups !== null && groups.length === 0}
          onChange={() => onChange({ kind: 'groups', groupIds: [] })}
          data-testid="publish-target-groups"
        />
        <span>
          Only these device groups
          {groups !== null && groups.length === 0 && (
            <span className="hint" style={{ display: 'block' }}>
              This organization has no device groups yet.
            </span>
          )}
        </span>
      </label>
      {targets.kind === 'groups' && groups && groups.length > 0 && (
        <ul className="binding-list" data-testid="publish-group-list">
          {groups.map((entry) => (
            <li key={entry.deviceGroupId}>
              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={chosen.includes(entry.deviceGroupId)}
                  onChange={(event) =>
                    onChange({
                      kind: 'groups',
                      groupIds: event.target.checked
                        ? [...chosen, entry.deviceGroupId]
                        : chosen.filter((id) => id !== entry.deviceGroupId)
                    })
                  }
                  data-testid={`publish-group-${entry.deviceGroupId}`}
                />
                <span>
                  {entry.name}{' '}
                  <span className="muted">
                    · {entry.memberCount} {entry.memberCount === 1 ? 'device' : 'devices'}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  )
}
